import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Link,
  Typography,
} from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import {
  EMAIL_CREDIT_ROLLOVER_CAP_DEFAULT,
  EMAIL_CREDIT_ROLLOVER_MONTHLY_DEFAULT,
  SubscriptionContext,
  SubscriptionTab,
} from "contexts/Subscription";
import { useContext } from "react";
import { SubscriptionType } from "schemas/dashboard";
import {
  DisplayName,
  PLAN_FAMILY,
  Plans,
  getEquivalentPlansFromOverride,
  isQuarterlyPlan,
} from "schemas/payments";

import { PRO_TERMS_AND_CONDITIONS_LINK } from "constants/influencer";
import { eligibleForProDiscount } from "features/Home/Banner/helpers";
import { trackEvent } from "utils/tracking";
import { useSubscription } from "utils/useSubscription";

import { B2GOCallout, B2GOPromoLine } from "../SharedComponents/B2GO";
import PlansPrice from "../SharedComponents/Price";
import {
  PlanAction,
  getCurrentPlanButton,
  getPlanLimitText,
  isCurrentPlan,
} from "../helpers";
import FollowUpAssistantItem from "./FollowUpAssistantItem";
import styles from "./styles";

interface Props {
  plan: Plans;
}

const ProPlanCard = ({ plan }: Props) => {
  const { currentOrg, organizationProProfile } = useContext(
    OrganizationUserContext,
  );
  const {
    plans,
    emailSents,
    subscription,
    tab,
    bannerPromoCodes,
    isAtEmailCreditCap,
  } = useContext(SubscriptionContext);
  const { onPlanButtonClick } = useSubscription();

  const planName = plans?.[plan]?.display_name;
  const totalEmails = plans?.[plan]?.monthly_limit || 0;
  const isB2GO = tab === SubscriptionTab.quarterly;
  const onQuarterlyPlan = isQuarterlyPlan(subscription?.subscription?.planName);
  const monthlyBase = Number(plans?.[Plans.PRO]?.price) || 0;
  const action = getCurrentPlanButton(
    plan,
    subscription?.subscription?.planName || Plans.FREE,
    subscription,
  );

  const trackTermsAndCondition = () => {
    trackEvent("Terms and Conditions for Pro Plan Clicked");
  };

  const BENEFITS = [
    {
      icon: "🚀",
      primary: (
        <>
          Send <strong>{totalEmails} emails</strong> per month
        </>
      ),
    },
    {
      icon: "🧠",
      primary: (
        <>
          Get <strong>Advanced Brand Filters</strong> (e.g "Brands that work
          with UGC creators") to refine your search
        </>
      ),
    },
    {
      icon: "📈",
      primary: (
        <>
          <strong> See reply likelihood scores</strong> for each contact
        </>
      ),
    },
    {
      icon: "🔑",
      primary: (
        <>
          Get access to <strong>600k+ Brand Contacts</strong>
        </>
      ),
    },
  ];

  const _isCurrentPlan =
    isCurrentPlan(subscription, plan) &&
    (subscription?.subscription?.status === SubscriptionType.ACTIVE ||
      subscription?.subscription?.status === SubscriptionType.PAST_DUE);

  const overridePlanName = currentOrg?.overridePlanName;
  const isOverridePlan = overridePlanName
    ? PLAN_FAMILY[plan] === overridePlanName
    : false;
  const overrideDisplayName = overridePlanName
    ? DisplayName[getEquivalentPlansFromOverride(overridePlanName)[0]]
    : "";

  if (!plans) return <></>;

  return (
    <>
      <Box sx={styles.card}>
        <Box sx={[styles.planTitleCard, { backgroundColor: "primary.main" }]}>
          <Grid container justifyContent="flex-end" sx={{ gap: 0.75 }}>
            <Chip
              sx={{ backgroundColor: "yellow.main", px: 1 }}
              label={
                <Typography variant="subtitle2" sx={{ fontSize: 13 }}>
                  <strong>Great for creators who pitch often</strong>
                </Typography>
              }
            ></Chip>
            {isB2GO && (
              <Chip
                sx={{ backgroundColor: "yellow.main", px: 1 }}
                label={
                  <Typography variant="subtitle2" sx={{ fontSize: 13 }}>
                    🎁 1 month free
                  </Typography>
                }
              />
            )}
          </Grid>

          <Typography variant="h3" sx={{ mt: 6, color: "white" }}>
            {planName}{" "}
            {_isCurrentPlan && (
              <Box component="span" sx={{ color: "yellow.main", fontSize: 16 }}>
                (Current Plan)
              </Box>
            )}
          </Typography>

          <Box sx={styles.price}>
            {tab === SubscriptionTab.monthly && <PlansPrice plan={Plans.PRO} />}
            {tab === SubscriptionTab.quarterly && (
              <PlansPrice plan={Plans.PRO_QUARTERLY} />
            )}
            {tab === SubscriptionTab.yearly && (
              <PlansPrice plan={Plans.PRO_YEARLY} />
            )}
          </Box>
          <Grid item xs={12}>
            <Link
              href={
                plans[plan]?.terms_and_conditions ||
                PRO_TERMS_AND_CONDITIONS_LINK
              }
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackTermsAndCondition}
              sx={{
                color: "yellow.main",
                fontSize: 10,
                textDecoration: "underline",
                textDecorationColor: "yellow.main",
              }}
            >
              Terms & Conditions
            </Link>
          </Grid>
          {isB2GO && <B2GOCallout monthlyBase={monthlyBase} />}
          {_isCurrentPlan && !isAtEmailCreditCap && (
            <Box sx={styles.rolloverBox}>
              <Box sx={{ ...styles.rolloverRow, mb: 0.5 }}>
                <Typography sx={styles.rolloverLabelDim}>
                  Email Credits Used
                </Typography>
                <Typography sx={styles.rolloverValue}>
                  {emailSents} /{" "}
                  {Math.max(emailSents, subscription?.monthlyLimit || 0)}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(
                  100,
                  (emailSents /
                    Math.max(1, subscription?.monthlyLimit || emailSents)) *
                    100,
                )}
                sx={styles.rolloverProgress}
              />
              <Box sx={{ ...styles.rolloverRow, mb: 0.25 }}>
                <Typography sx={styles.rolloverLabel}>
                  Monthly Allotment
                </Typography>
                <Typography sx={styles.rolloverLabel}>
                  {(
                    (subscription?.monthlyLimit || 0) -
                    (subscription?.rolloverEmailCredits || 0)
                  ).toLocaleString()}
                </Typography>
              </Box>
              {(subscription?.rolloverEmailCredits || 0) > 0 && (
                <Box sx={styles.rolloverRow}>
                  <Typography sx={styles.rolloverLabel}>
                    Rolled over from last month
                  </Typography>
                  <Typography sx={styles.rolloverValueAccent}>
                    +{subscription!.rolloverEmailCredits!.toLocaleString()}
                  </Typography>
                </Box>
              )}
              <Divider sx={styles.rolloverDivider} />
              <Box sx={styles.rolloverRow}>
                <Typography sx={styles.rolloverTotal}>
                  Total available this month
                </Typography>
                <Typography sx={styles.rolloverTotal}>
                  {Math.max(
                    emailSents,
                    subscription?.monthlyLimit || 0,
                  ).toLocaleString()}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {!isAtEmailCreditCap && (
          <Box sx={styles.rolloverInfoBox}>
            <Box sx={styles.rolloverInfoHeader}>
              <Box
                component="i"
                className="fa-solid fa-arrow-trend-up"
                sx={styles.rolloverInfoIcon}
              />
              <Typography sx={styles.rolloverInfoTitle}>
                {_isCurrentPlan ? "Credit Rollover Active" : "Rollover Credits"}
              </Typography>
            </Box>
            <Typography sx={styles.rolloverInfoDesc}>
              {`Up to ${EMAIL_CREDIT_ROLLOVER_MONTHLY_DEFAULT} unused emails roll over monthly. Build up to a max of ${EMAIL_CREDIT_ROLLOVER_CAP_DEFAULT.toLocaleString()} total emails.`}
            </Typography>
          </Box>
        )}

        {BENEFITS.map((benefit, index) => (
          <Grid container key={index} alignItems="center" sx={{ mb: 1.5 }}>
            <Grid
              container
              justifyContent="left"
              alignItems="center"
              item
              xs="auto"
            >
              <Box sx={styles.iconBlock}>{benefit.icon}</Box>
            </Grid>
            <Grid item xs md>
              <Typography component="div">{benefit.primary}</Typography>
            </Grid>
          </Grid>
        ))}
        <FollowUpAssistantItem
          isCurrentPlan={_isCurrentPlan}
          subscription={subscription}
        />

        {_isCurrentPlan &&
          organizationProProfile?.planConfig?.monthlyLimit &&
          organizationProProfile.planConfig.monthlyLimit !== totalEmails && (
            <Alert severity="info">
              <Typography
                sx={{
                  fontSize: 12,
                  color: "black",

                  fontWeight: 500,
                }}
              >
                As an existing subscriber, your Pro plan includes{" "}
                <strong>
                  {organizationProProfile.planConfig.monthlyLimit} emails/month
                </strong>{" "}
              </Typography>
            </Alert>
          )}

        {overridePlanName ? (
          <Box
            sx={{
              mt: 3,
              py: 1,
              borderRadius: 2,
              textAlign: "center",
              backgroundColor: "#f4f8f6",
            }}
          >
            <Typography
              sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }}
            >
              <Box
                component="i"
                className={
                  isOverridePlan
                    ? "fa-solid fa-circle-check"
                    : "fa-solid fa-circle-info"
                }
                sx={{ mr: 0.75, color: "primary.main" }}
              />
              {isOverridePlan
                ? `You're on the ${overrideDisplayName} plan`
                : `Unable to subscribe. You're on an override plan (${overrideDisplayName})`}
            </Typography>
          </Box>
        ) : (
          <Button
            disableElevation
            fullWidth
            variant={action === PlanAction.upgrade ? "contained" : "text"}
            sx={{
              mt: "auto",
              py: 1,
              borderRadius: 2,
              backgroundColor:
                action === PlanAction.upgrade ? "primary.main" : "#f4f8f6",
            }}
            onClick={() => onPlanButtonClick(plan, action)}
          >
            {action}{" "}
          </Button>
        )}
      </Box>

      <Box sx={{ mt: 2 }}>
        {isB2GO && !onQuarterlyPlan ? (
          <B2GOPromoLine />
        ) : (
          getPlanLimitText(
            subscription,
            plan,
            emailSents,
            plans,
            bannerPromoCodes,
            eligibleForProDiscount(currentOrg, plans, subscription),
          )
        )}
      </Box>
    </>
  );
};

export default ProPlanCard;
